import { useEffect, useMemo, useState } from 'react';
import { Segmented, Select, Typography } from 'antd';

import ItemLookupSelect from '../common/ItemLookupSelect';
import { describeToken, lookupValueRefusal, parseLookupValue, recordsValue, refusalSentence } from '../../utils/lookupSources';

const { Text } = Typography;

// The option that stands for a stored value no source in the list is: kept
// as it is until the person picks something else, never shown as its text.
const STORED = '__stored-value__';

/**
 * THE VALUE OF A LOOKUP A RULE WRITES (keepr-api KPR-1104: text never fills a
 * lookup). Two ways to fill one, and only these:
 *
 *   Copy a link       one source the rule copies a record from — a lookup of
 *                     the item that set it off (or one step through it), the
 *                     item itself, each matching item and its lookups in a
 *                     fan-out, the entry's lookup columns — picked from a list
 *                     by its label (`sources`, utils/lookupSources.js
 *                     lookupSourceOptions). Never typed.
 *   Choose a record   the records the manager picks by title (ItemLookupSelect,
 *                     ids only: its title escape is off) — one, or a list for a
 *                     lookup that holds several.
 *
 * A value saved before this that is neither (a text element copied into the
 * lookup, typed text) opens FLAGGED on this field, in words, and is kept until
 * the person picks something else; the API refuses to save it.
 */
export default function LookupSourceField( {
	value,
	onChange,
	target,
	sources = [],
	ctx = {},
	collectionId,
	ariaLabel,
	targetCardName = '',
	note = '',
} ) {
	const multiple = target?.options?.allowMultiple === true;
	const parsed = useMemo( () => parseLookupValue( value ), [ value ] );
	const refusal = useMemo( () => lookupValueRefusal( value, ctx ), [ value, ctx ] );
	const [ mode, setMode ] = useState( parsed.kind === 'records' ? 'records' : 'link' );
	// A value that arrives from outside (the editor seeding a stored rule) says
	// which way it was filled; a blank one leaves the choice where it is.
	useEffect( () => {
		if ( parsed.kind === 'records' ) setMode( 'records' );
		else if ( parsed.kind === 'link' || parsed.kind === 'other' ) setMode( 'link' );
	}, [ parsed.kind ] );

	const offered = useMemo( () => sources.flatMap( group => group.options || [] ), [ sources ] );
	const linkOptions = useMemo( () => {
		const out = [ ...sources ];
		// A stored source the list does not offer: shown in words and kept —
		// flagged when the API would refuse it.
		if ( parsed.kind === 'link' && ! offered.some( option => option.value === parsed.token ) ) {
			out.unshift( { value: parsed.token, label: `${ describeToken( parsed.token, ctx ) }${ refusal ? ' (cannot fill a lookup)' : '' }`, disabled: true } );
		}
		if ( parsed.kind === 'other' ) {
			out.unshift( { value: STORED, label: refusal?.reason === 'not_an_id' ? 'Typed text (cannot fill a lookup)' : 'A mix of sources and text (cannot fill a lookup)', disabled: true } );
		}
		return out;
	}, [ sources, offered, parsed, ctx, refusal ] );

	const linkValue = parsed.kind === 'link' ? parsed.token : ( parsed.kind === 'other' ? STORED : undefined );
	const ids = parsed.kind === 'records' ? parsed.ids : [];
	const cardId = target?.options?.lookupCardId || '';
	const label = ariaLabel || 'Lookup value';

	return (
		// Laid out by src/css/components/lookup-source.css: a column as wide as a
		// value box, whose warning wraps; 44px targets on a touch screen.
		<div className='lookup-source-field'>
			<Segmented
				size='small'
				aria-label={`How ${ label.toLowerCase() } is filled`}
				value={mode}
				options={[
					{ value: 'link', label: 'Copy a link' },
					{ value: 'records', label: multiple ? 'Choose records' : 'Choose a record' },
				]}
				onChange={next => {
					setMode( next );
					// Switching ways starts the value over: a link and a chosen
					// record are two different sources, never one value.
					if ( next !== mode ) onChange?.( next === 'records' && multiple ? [] : '' );
				}}
			/>
			{mode === 'link' ? (<>
				<Select
					style={{ width: '100%' }}
					value={linkValue}
					options={linkOptions}
					allowClear
					showSearch
					optionFilterProp='label'
					status={refusal ? 'warning' : undefined}
					aria-label={label}
					placeholder='Copy the link from…'
					notFoundContent='Nothing here links to that card'
					onChange={next => {
						if ( next === STORED ) return;
						onChange?.( next || '' );
					}}
				/>
				{! offered.length && parsed.kind !== 'other' ? (
					<Text type='secondary' className='d-block'>
						{`Nothing the rule reads links to ${ targetCardName || 'this lookup\'s card' }. Choose ${ multiple ? 'records' : 'a record' } instead.`}
					</Text>
				) : null}
			</>) : (
				cardId ? (
					<ItemLookupSelect
						collectionId={collectionId}
						cardId={cardId}
						multiple={multiple}
						titles={false}
						value={multiple ? ids : ( ids[ 0 ] || '' )}
						ariaLabel={label}
						placeholder={multiple ? 'Pick records' : 'Pick a record'}
						status={refusal ? 'warning' : undefined}
						style={{ width: '100%' }}
						onChange={next => onChange?.( recordsValue( next, multiple ) )}
					/>
				) : (
					<Text type='secondary' className='d-block'>This lookup names no card to choose a record from.</Text>
				)
			)}
			{note && mode === 'link' ? <Text type='secondary' className='d-block'>{note}</Text> : null}
			{refusal ? <Text type='warning' className='d-block'>{refusalSentence( refusal, ctx )}</Text> : null}
		</div>
	);
}
